import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, X, Loader2, Trophy, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/referrals`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX = 5;

export const InviteForm = ({ onSent }) => {
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const addDraft = () => {
    const parts = draft.split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const bad = parts.filter(p => !EMAIL_RE.test(p));
    if (bad.length) toast.error(`Not a valid email: ${bad[0]}`);
    const next = [...new Set([...emails, ...parts.filter(p => EMAIL_RE.test(p))])];
    if (next.length > MAX) toast.error(`You can invite up to ${MAX} friends at a time`);
    setEmails(next.slice(0, MAX));
    setDraft(bad.join(' '));
  };

  const send = async (e) => {
    e.preventDefault();
    const pending = draft.trim() ? [...new Set([...emails, ...draft.split(/[\s,;]+/).filter(p => EMAIL_RE.test(p.toLowerCase())).map(p => p.toLowerCase())])].slice(0, MAX) : emails;
    if (!pending.length) return toast.error('Add at least one email');
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/invite`, { emails: pending, note: note.trim() || null }, { withCredentials: true });
      const skipped = data.results.filter(r => r.status !== 'sent');
      if (data.sent) toast.success(`Invite${data.sent === 1 ? '' : 's'} sent to ${data.sent} friend${data.sent === 1 ? '' : 's'}`);
      skipped.forEach(r => toast.message(`${r.email}: ${r.reason}`));
      setEmails([]); setDraft(''); setNote('');
      onSent?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send invites');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={send} className="space-y-3" data-testid="referral-invite-form">
      <div className="flex min-h-[44px] flex-wrap items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-emerald-500">
        {emails.map(em => (
          <span key={em} className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs text-emerald-900" data-testid="referral-invite-chip">
            {em}
            <button type="button" onClick={() => setEmails(emails.filter(x => x !== em))} aria-label={`Remove ${em}`} className="text-emerald-700 hover:text-emerald-900"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {emails.length < MAX && (
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => draft.trim() && addDraft()}
            onKeyDown={(e) => { if (['Enter', ',', ' '].includes(e.key) && draft.trim()) { e.preventDefault(); addDraft(); } }}
            placeholder={emails.length ? 'Add another…' : 'friend@example.com'}
            className="h-8 min-w-[12rem] flex-1 border-0 px-1 shadow-none focus-visible:ring-0" data-testid="referral-invite-email-input" />
        )}
      </div>
      <Textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Add a personal note (optional) — e.g. “This saved me hours at tax time!”" data-testid="referral-invite-note" />
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-stone-500">Press Enter to add up to 5 emails. Max 20 invites a day; friends won't be re-invited within 30 days.</p>
        <Button type="submit" disabled={sending} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="referral-invite-send-btn">
          {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send invites
        </Button>
      </div>
    </form>
  );
};

export const Leaderboard = ({ data, onToggleHidden }) => (
  <section className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="referral-leaderboard">
    <div className="flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 text-base font-semibold text-stone-900"><Trophy className="h-4 w-4 text-amber-500" /> Top referrers this month</h2>
      <label className="flex cursor-pointer items-center gap-2 text-xs text-stone-600">
        {data.me.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} Show me
        <Switch checked={!data.me.hidden} onCheckedChange={(v) => onToggleHidden(!v)} data-testid="referral-leaderboard-visibility" />
      </label>
    </div>
    {data.entries.length === 0 ? (
      <p className="mt-4 text-sm text-stone-500" data-testid="referral-leaderboard-empty">No paid referrals yet this month — be the first!</p>
    ) : (
      <ol className="mt-4 space-y-1.5">
        {data.entries.map(e => (
          <li key={e.rank} className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${e.is_me ? 'bg-emerald-50 ring-1 ring-emerald-200' : ''}`} data-testid={`referral-leaderboard-row-${e.rank}`}>
            <span className={`w-6 text-center font-mono font-bold ${e.rank <= 3 ? 'text-amber-500' : 'text-stone-400'}`}>{e.rank}</span>
            <span className="flex-1 text-stone-800">{e.name}{e.is_me && <span className="ml-1.5 text-xs text-emerald-700">(you)</span>}</span>
            <span className="font-mono tabular-nums text-stone-600">{e.count}</span>
          </li>
        ))}
      </ol>
    )}
    <p className="mt-4 text-xs text-stone-500" data-testid="referral-leaderboard-me">
      {data.me.hidden ? "You're hidden from the board." : data.me.rank ? `You're #${data.me.rank} with ${data.me.count} paid referral${data.me.count === 1 ? '' : 's'} this month.` : 'Refer a friend who subscribes to join the board.'}
      {' '}Only first names and last initials are shown.
    </p>
  </section>
);
